import { Namespace, useT } from "@racoon/i18n";

type ProgressBarItem = {
  value: number;
  color?: string;
  label?: string;
};

type ProgressBarProps = {
  items: ProgressBarItem[];
  valueType?: "value" | "percentage";
  minSegmentPct?: number;
  minSegmentWidthPx?: number;
  hideLabelBelowPct?: number;
  heightClassName?: string;
  containerClassName?: string;
  segmentClassName?: string;
  formatValue?: (value: number, index: number) => string;
};

export const ProgressBar = ({
  items,
  valueType = "value",
  minSegmentPct,
  minSegmentWidthPx,
  hideLabelBelowPct,
  heightClassName = "h-8",
  containerClassName = "",
  segmentClassName = "",
  formatValue,
}: ProgressBarProps) => {
  const t = useT();
  const segments = items
    .map((item) => {
      const numericValue = Number(item.value);
      const value = Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
      return {
        value,
        rawValue: numericValue,
        label: item.label,
        color: item.color,
      };
    })
    .filter((item) => item.value > 0);

  const total = segments.reduce((sum, item) => sum + item.value, 0);

  const withPct = segments.map((segment) => ({
    ...segment,
    rawPct: total > 0 ? (segment.value / total) * 100 : 0,
  }));

  const withMinPct =
    typeof minSegmentPct === "number" && minSegmentPct > 0
      ? withPct.map((segment) => ({
          ...segment,
          widthPct: segment.value > 0 ? Math.max(segment.rawPct, minSegmentPct) : 0,
        }))
      : withPct.map((segment) => ({ ...segment, widthPct: segment.rawPct }));

  const widthSum = withMinPct.reduce((sum, segment) => sum + segment.widthPct, 0);
  const normalized = withMinPct.map((segment) => ({
    ...segment,
    normalizedWidthPct: widthSum > 0 ? (segment.widthPct / widthSum) * 100 : 0,
  }));

  const resolveLabel = (value: number, rawPct: number, index: number) => {
    if (valueType === "percentage") {
      return `${Math.round(rawPct)}%`;
    }
    if (typeof formatValue === "function") {
      return formatValue(value, index);
    }
    return String(Math.round(value));
  };

  const hasNoData = normalized.length === 0;

  return (
    <div className={containerClassName}>
      <div
        className={`flex rounded-lg overflow-hidden border border-gray-200 ${heightClassName} ${hasNoData ? "items-center justify-center w-full bg-gray-300" : ""}`}
      >
        {hasNoData ? (
          <span className="text-white text-xs font-medium px-2">
            {t(Namespace.Common, "noData")}
          </span>
        ) : (
          normalized.map((segment, index) => {
            const showLabel =
              typeof hideLabelBelowPct !== "number" ||
              segment.normalizedWidthPct >= hideLabelBelowPct;

            return (
              <div
                key={`${segment.color}-${index}`}
                className={`${segment.color} flex items-center justify-center ${segmentClassName}`.trim()}
                style={{
                  width: `${segment.normalizedWidthPct}%`,
                  minWidth: minSegmentWidthPx ? `${minSegmentWidthPx}px` : undefined,
                }}
              >
                {showLabel ? (
                  <span className="text-white text-xs font-medium">
                    {segment.label ?? resolveLabel(segment.value, segment.rawPct, index)}
                  </span>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
